import React, { useEffect, useState } from "react";
import ImagePicker from "./ImagePicker";
import resolveImageUrl from "@/lib/imageUrl";

const pickedUrl = (asset) => (typeof asset === "string" ? asset : (asset?.url || asset?.path || ""));

/**
 * Shirt product admin — edits the public /raffle T-shirt block and the
 * standalone /shirts page via `kind="shirt_product"`.
 *
 * Fields: title + description (EN/DA/ES), image + alt, per-size availability.
 * Image is only rendered publicly when set; a blank URL keeps the block
 * image-less rather than showing a broken frame.
 */
const SIZES = ["XS", "S", "M", "L", "XL", "XXL"];

export default function ShirtProductAdmin({ authed }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  // null = closed, "legacy" = legacy single image, number = gallery index.
  const [pickerTarget, setPickerTarget] = useState(null);

  useEffect(() => {
    authed.get("/admin/shirts/product").then(r => setData(r.data)).catch(() => setData(null));
  }, []);

  const save = async () => {
    setSaving(true); setMsg("");
    try {
      const r = await authed.put("/admin/shirts/product", data);
      setData(r.data);
      setMsg("Saved. Refresh the public page to see it live.");
    } catch (e) {
      setMsg(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!data) return <div className="text-ink/50 text-sm" data-testid="shirt-product-loading">Loading…</div>;

  const setI18n = (key, lang, val) =>
    setData(x => ({ ...x, [key]: { ...(x[key] || {}), [lang]: val } }));

  const setAvail = (size, v) =>
    setData(x => ({ ...x, size_availability: { ...(x.size_availability || {}), [size]: v } }));

  const addImage = () =>
    setData(x => ({ ...x, images: [...(x.images || []), { url: "", alt: { en: "", da: "", es: "" }, show: true }] }));
  const removeImage = (i) =>
    setData(x => { const next = [...(x.images || [])]; next.splice(i, 1); return { ...x, images: next }; });
  const moveImage = (i, dir) =>
    setData(x => {
      const next = [...(x.images || [])];
      const j = i + dir;
      if (j < 0 || j >= next.length) return x;
      [next[i], next[j]] = [next[j], next[i]];
      return { ...x, images: next };
    });
  const updateImage = (i, patch) =>
    setData(x => { const next = [...(x.images || [])]; next[i] = { ...next[i], ...patch }; return { ...x, images: next }; });

  return (
    <div className="space-y-6" data-testid="shirt-product-admin">
      <section className="border border-line rounded-xl p-6 bg-white">
        <div className="label-mono text-terracotta text-[10px]">T-SHIRT PRODUCT</div>
        <h2 className="font-editorial text-2xl md:text-3xl tracking-tighter mt-1">T-shirt (public /raffle + /shirts)</h2>
        <p className="mt-2 text-xs text-ink/60 max-w-xl">
          Edit the T-shirt product shown on the /raffle page and the standalone /shirts page.
          Leave the image blank until you have uploaded an approved photo — the public block hides
          the image frame automatically. A size switched off is rejected on checkout.
        </p>
      </section>

      <Box title="Product title (public heading)">
        {["en", "da", "es"].map(lng => (
          <TextRow key={lng} label={`Title · ${lng.toUpperCase()}`}
                   value={data.title?.[lng] || ""}
                   testid={`shirt-title-${lng}`}
                   onChange={v => setI18n("title", lng, v)} />
        ))}
      </Box>

      <Box title="Product description (optional, 2–3 lines)">
        {["en", "da", "es"].map(lng => (
          <AreaRow key={lng} label={`Description · ${lng.toUpperCase()}`}
                   value={data.description?.[lng] || ""}
                   testid={`shirt-desc-${lng}`}
                   onChange={v => setI18n("description", lng, v)} />
        ))}
      </Box>

      <Box title="Product gallery (ordered)" hint="Add one or more product photos. Each photo has EN/DA/ES alt text and a visibility toggle. The first visible image is the main photo; the rest become thumbnails on /raffle. If the gallery has at least one visible image it is used; otherwise the legacy single image below is shown. The image column is hidden only when both are empty. Removing a photo here only takes it out of the gallery — the file stays in the media library.">
        {(data.images || []).length === 0 && (
          <div className="label-mono text-ink/50 text-xs" data-testid="shirt-gallery-empty">No gallery images — /raffle uses the legacy single image below if one is set.</div>
        )}
        {(data.images || []).map((img, i) => (
          <div key={i} className="border border-line rounded p-3 mb-3" data-testid={`shirt-gallery-item-${i}`}>
            <div className="flex items-start gap-3">
              <div className="w-24 h-24 border border-line rounded bg-line/10 overflow-hidden flex items-center justify-center shrink-0">
                {img.url
                  ? <img src={resolveImageUrl(img.url)} alt="" className="w-full h-full object-cover" data-testid={`shirt-gallery-preview-${i}`} />
                  : <span className="label-mono text-ink/40 text-[10px]">NO IMAGE</span>}
              </div>
              <div className="flex-1 space-y-2">
                <button type="button" onClick={() => setPickerTarget(i)} data-testid={`shirt-gallery-pick-${i}`}
                        className="inline-flex items-center gap-2 border border-ink text-ink hover:bg-ink hover:text-white px-3 py-1.5 text-xs tracking-wide">
                  {img.url ? "Replace (upload / choose)" : "Upload / Choose image"}
                </button>
                <TextRow label="Image URL (/api/media/… or /images/…)"
                         value={img.url || ""}
                         testid={`shirt-gallery-url-${i}`}
                         onChange={v => updateImage(i, { url: v })} />
                <div>
                  <label className="label-mono text-ink/60 text-[10px] tracking-widest">Alt text (EN / DA / ES)</label>
                  {["en", "da", "es"].map(lng => (
                    <input key={lng}
                           value={(img.alt && img.alt[lng]) || ""}
                           onChange={e => updateImage(i, { alt: { ...(img.alt || {}), [lng]: e.target.value } })}
                           placeholder={lng.toUpperCase()}
                           data-testid={`shirt-gallery-alt-${i}-${lng}`}
                           className="mt-1 w-full border border-line px-3 py-1.5 text-sm" />
                  ))}
                </div>
                <div className="flex items-center gap-3 pt-1">
                  <label className="inline-flex items-center gap-2 text-xs">
                    <input type="checkbox" checked={img.show !== false}
                           onChange={e => updateImage(i, { show: e.target.checked })}
                           data-testid={`shirt-gallery-show-${i}`} />
                    Visible
                  </label>
                  <button type="button" onClick={() => moveImage(i, -1)} data-testid={`shirt-gallery-up-${i}`} className="text-xs border border-line px-2 py-1 hover:border-ink">↑</button>
                  <button type="button" onClick={() => moveImage(i, +1)} data-testid={`shirt-gallery-down-${i}`} className="text-xs border border-line px-2 py-1 hover:border-ink">↓</button>
                  <button type="button" onClick={() => removeImage(i)} data-testid={`shirt-gallery-remove-${i}`} className="text-xs text-red-600 hover:underline ml-auto">Remove from gallery</button>
                </div>
              </div>
            </div>
          </div>
        ))}
        <button type="button" onClick={addImage} data-testid="shirt-gallery-add"
                className="mt-1 inline-flex items-center gap-2 border border-ink text-ink hover:bg-ink hover:text-white px-4 py-2 text-xs tracking-wide">
          + Add image
        </button>
      </Box>

      <Box title="Product image (legacy single)" hint="Kept for compatibility. Used on /raffle only when the gallery above has no visible image. Clearing it does not delete the file from the media library.">
        <div className="flex items-start gap-4">
          <div className="w-28 h-28 border border-line rounded bg-line/10 overflow-hidden flex items-center justify-center">
            {data.image ? (
              <img src={resolveImageUrl(data.image)} alt="" className="w-full h-full object-cover" data-testid="shirt-image-preview" />
            ) : (
              <span className="label-mono text-ink/40 text-[10px]">NO IMAGE</span>
            )}
          </div>
          <div className="flex-1 flex flex-col gap-2">
            <button type="button" onClick={() => setPickerTarget("legacy")} data-testid="shirt-image-pick"
                    className="self-start inline-flex items-center gap-2 border border-ink text-ink hover:bg-ink hover:text-white px-4 py-2 text-xs tracking-wide">
              {data.image ? "Replace image" : "Upload / Choose image"}
            </button>
            {data.image && (
              <button type="button" onClick={() => setData(x => ({ ...x, image: "" }))}
                      data-testid="shirt-image-clear"
                      className="self-start text-xs text-red-600 hover:underline">Clear field</button>
            )}
            <TextRow label="Or paste image URL manually"
                     value={data.image || ""}
                     testid="shirt-image-url"
                     onChange={v => setData(x => ({ ...x, image: v }))} />
          </div>
        </div>
        <div className="mt-4">
          {["en", "da", "es"].map(lng => (
            <TextRow key={lng} label={`Legacy image alt text · ${lng.toUpperCase()}`}
                     value={data.image_alt?.[lng] || ""}
                     testid={`shirt-alt-${lng}`}
                     onChange={v => setI18n("image_alt", lng, v)} />
          ))}
        </div>
      </Box>

      <Box title="Size availability" hint="Sizes XS to XXL. Toggle a size off to prevent purchase without hiding the public stock count.">
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {SIZES.map(s => (
            <label key={s} data-testid={`shirt-avail-${s}`}
                   className={`flex items-center gap-2 border rounded px-3 py-2 cursor-pointer ${(data.size_availability?.[s] !== false) ? "border-terracotta bg-terracotta/5" : "border-line opacity-60"}`}>
              <input type="checkbox"
                     checked={data.size_availability?.[s] !== false}
                     onChange={e => setAvail(s, e.target.checked)}
                     className="w-4 h-4 accent-terracotta" />
              <span className="font-editorial text-base">{s}</span>
            </label>
          ))}
        </div>
      </Box>

      <div className="flex items-center justify-end gap-3 sticky bottom-0 bg-cream/90 backdrop-blur -mx-6 px-6 py-4 border-t border-line">
        {msg && <span className="text-xs text-ink/60" data-testid="shirt-product-msg">{msg}</span>}
        <button onClick={save} disabled={saving} data-testid="shirt-product-save"
                className="bg-terracotta hover:bg-terracotta-dark disabled:opacity-60 text-white px-6 py-2.5 text-sm tracking-wide rounded-full">
          {saving ? "Saving…" : "Save"}
        </button>
      </div>

      {pickerTarget !== null && (
        <ImagePicker authed={authed}
                     onPick={(asset) => {
                       const url = pickedUrl(asset);
                       if (pickerTarget === "legacy") setData(x => ({ ...x, image: url }));
                       else updateImage(pickerTarget, { url });
                       setPickerTarget(null);
                     }}
                     onClose={() => setPickerTarget(null)} />
      )}
    </div>
  );
}

function Box({ title, hint, children }) {
  return (
    <section className="border border-line rounded-xl p-6 bg-white">
      <div className="font-editorial text-xl tracking-tighter">{title}</div>
      {hint && <p className="text-xs text-ink/60 mt-1 max-w-lg">{hint}</p>}
      <div className="mt-4 space-y-3">{children}</div>
    </section>
  );
}

function TextRow({ label, value, placeholder, testid, onChange }) {
  return (
    <div>
      <label className="label-mono text-ink/60 text-[10px] tracking-widest">{label}</label>
      <input value={value} placeholder={placeholder || ""} data-testid={testid}
             onChange={e => onChange(e.target.value)}
             className="mt-1 w-full border border-line px-3 py-2 text-sm" />
    </div>
  );
}

function AreaRow({ label, value, testid, onChange }) {
  return (
    <div>
      <label className="label-mono text-ink/60 text-[10px] tracking-widest">{label}</label>
      <textarea value={value} data-testid={testid} rows={2}
                onChange={e => onChange(e.target.value)}
                className="mt-1 w-full border border-line px-3 py-2 text-sm resize-y min-h-[48px]" />
    </div>
  );
}
